import { Component, Inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControlStatus, FormGroup, Validators } from "@angular/forms";
import {
    keysSensorEventActuatorType,
    SensorEventActuator,
    SensorEventActuatorType,
    SensorEventModalityNotificationType,
    SensorEventParse,
    SensorEventStatement,
    SensorEventStatementType,
    SensorEventType
} from "../../../ParseClass/SensorEvent.Parse";
import {
    arrayIsSet, deepClone,
    getOnlyValuesEnum,
    KeyStringValue,
    stringIsSet
} from "../../../providers/service/global-variables.service";
import { IconService } from "../../../providers/service/icon.service";
import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { filter, map, startWith } from "rxjs/operators";
import { ElementTypeParse } from "../../../ParseClass/ElementType.Parse";
import { ElementParse } from "../../../ParseClass/Element.Parse";
import { OpenDialogService } from "../../../providers/service/open-dialog.service";
import { TranslateService } from "@ngx-translate/core";
import { HunaValidators } from "../../../providers/validators/HunaValidators";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Light } from "../../../ParseClass/light";
import { Shutter } from "../../../ParseClass/shutter";
import { Switch } from "../../../ParseClass/switch";
import { DevicesDividedForCategory } from "../../../providers/service/sensor-event-page.service";
import { PossibleValuesFormField } from "../../../widget/multi-select-form-field/multi-select-form-field.component";
import { ButtonFloatingType } from "../../../widget/floating-buttons/floating-buttons.component";
import { dataForm } from "../../../widget/auto-complete-form/auto-complete-form.component";
import { Command } from "../../../ParseClass/command";
import { CalendarEvent } from "../../../ParseClass/calendar-event";
import { LinearActuator } from "../../../ParseClass/linear-actuator";

export type ActuatorsHtmlType = {
    all: SensorEventActuator[],
    divided: {
        title: string;
        svgIcon?: string,
        icon?: string;
        actuators: { actuator: SensorEventActuator, indexAll: number }[]
    }[]
}
export type ChipsHtmlType = {
    sx: { infoNameTraduction: string; sensorName?: string; type?: string; op?: string };
    operator: string;
    dx: { infoNameTraduction?: string; sensorName?: string; value?: { notTraduction?: number; traduction?: string; }; }
}

@Component({
    selector: 'app-sensor-event-form',
    templateUrl: './sensor-event-form.component.html',
    styleUrls: ['./sensor-event-form.component.scss'],
    standalone: false
})
export class SensorEventFormComponent implements OnInit {
    @ViewChild('ModifyEnablingStatement') private ModifyEnablingStatementRef: TemplateRef<any> | undefined;
    @ViewChild('ModifyStatement') private ModifyStatementRef: TemplateRef<any> | undefined;
    @ViewChild('ModifyActuators') private ModifyActuatorsRef: TemplateRef<any> | undefined;
    private modifyDialogRef: MatDialogRef<any> | undefined

    sensorDataType: dataForm[] | undefined;
    calendars: CalendarEvent[] | undefined;
    public sensorEventType = SensorEventType
    public form: FormGroup
    public formSingleStatement: FormGroup
    public elements: ElementParse[] | undefined;
    public lights: Light[] | undefined;
    public shutters: Shutter[] | undefined;
    public switches: Switch[] | undefined;
    public commands: Command[] | undefined;
    public linearActuators: LinearActuator[] | undefined;
    public elementsType: ElementTypeParse[] | undefined;
    public statements$: Observable<SensorEventStatement[][] | undefined>
    public enablingStatements$: Observable<SensorEventStatement[][] | undefined>
    public actuators$: Observable<SensorEventActuator[] | undefined>
    public actuatorsDivided$: Observable<ActuatorsHtmlType | undefined>
    public elseActuatorsDivided$: Observable<ActuatorsHtmlType | undefined>
    lastAccordionOpenedEmit = new BehaviorSubject<{
        actuators?: string;
        else_actuators?: string
    } | undefined>(undefined)
    lastAccordionActuatorsOpened$ = this.lastAccordionOpenedEmit.asObservable().pipe(
        map(values => {
            if (values != null && stringIsSet(values.actuators)) {
                return values.actuators
            } else {
                return undefined
            }
        })
    );
    lastAccordionElseActuatorsOpened$ = this.lastAccordionOpenedEmit.asObservable().pipe(
        map(values => {
            if (values != null && stringIsSet(values.else_actuators)) {
                return values.else_actuators
            } else {
                return undefined
            }
        })
    );
    public fieldsInErrors$: Observable<{ errors: KeyStringValue<string[]> }>

    keysActuatorsType: keysSensorEventActuatorType[] = Object.values(SensorEventActuatorType)
        .filter(value => typeof value === 'string') as keysSensorEventActuatorType[]
    public else_actuators$: Observable<SensorEventActuator[] | undefined>
    breakPoints = [
        {
            maxWidth: 1000,
            sx: { percent: 50, px: undefined },
            dx: { percent: 50, px: undefined },
        },
        {
            maxWidth: 650,
            sx: { percent: 100, px: undefined },
            dx: { percent: 100, px: undefined },
        }
    ];

    possibleValuesSensorEventModalityNotificationType = getOnlyValuesEnum(SensorEventModalityNotificationType).map(val => {
        return new ButtonFloatingType(val, parseInt(val), 'SensorEvent.modality.' + val)
    })
    isEditable: boolean

    constructor(
        public dialogRef: MatDialogRef<SensorEventFormComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {
            sensorEvent?: SensorEventParse,
            calendars?: CalendarEvent[],
            elements?: ElementParse[],
            elementsType: ElementTypeParse[],
            elementsCategory: DevicesDividedForCategory
            users: { objectId: string, name: string },
            sensorDataType: dataForm[],
            isEditable: boolean | undefined
        },
        private fb: FormBuilder,
        private dialog: MatDialog,
        private ts: TranslateService,
        iconService: IconService,
        private openDialogService: OpenDialogService
    ) {
        iconService.addSvgIcon('and', 'assets/icon/logic/andIcon.svg')
        iconService.addSvgIcon('or', 'assets/icon/logic/orIcon.svg')
        iconService.addSvgIcon(SensorEventActuatorType.EXT_SENSOR.toString(), 'assets/icon/sensor/external_sensor.svg')
        iconService.addSvgIcon(SensorEventActuatorType.SHUTTER.toString(), 'assets/icon/sensor/blinds.svg')
        iconService.addSvgIcon(SensorEventActuatorType.LIGHT.toString(), 'assets/icon/sensor/bulbs.svg')
        iconService.addSvgIcon(SensorEventActuatorType.LINEAR_ACTUATOR.toString(), 'assets/icon/sensor/curvaClimatica.svg')
        iconService.addSvgIcon('=', 'assets/icon/math/equals.svg');
        iconService.addSvgIcon('!=', 'assets/icon/math/notEquals.svg');
        iconService.addSvgIcon('>', 'assets/icon/math/greather.svg');
        iconService.addSvgIcon('>=', 'assets/icon/math/greatherOrEquals.svg');
        iconService.addSvgIcon('<', 'assets/icon/math/less.svg');
        iconService.addSvgIcon('<=', 'assets/icon/math/lessOrEquals.svg');
        iconService.addSvgIcon('d!=', 'assets/icon/math/d!=.svg')
        iconService.addSvgIcon('t!=', 'assets/icon/math/t!=.svg')
        iconService.addSvgIcon('det!=', 'assets/icon/math/det!=.svg')
        iconService.addSvgIcon('dot!=', 'assets/icon/math/dot!=.svg')
        iconService.addSvgIcon('winterSummer', '/assets/icon/temperature/estateInverno.svg')

        this.form = this.fb.group({
            name: [undefined, [Validators.required]],
            note: undefined,
            hysteresis: undefined,
            deactivationHysteresis: undefined,
            sendSms: undefined,
            notification: fb.group({ body: undefined, subject: undefined }),
            type: [undefined, [Validators.required]],
            repeatEvent: [false, [Validators.required]],
            notificationUsers: undefined,
            enablingStatements: [undefined, []],
            statements: [undefined, [Validators.required, HunaValidators.arrayOfArrayIsSet('arrayEmptyStatement')]],
            actuators: [undefined,],
            else_actuators: undefined,
            modalityNotification: undefined,
        })
        this.isEditable = this.data.isEditable == true
        if (!this.isEditable) {
            this.form.disable()
        }
        this.fieldsInErrors$ = this.form.statusChanges.pipe(
            startWith(this.form.valid),
            map(status => {
                if (status == "INVALID") {
                    const keys = Object.keys(this.form.value)
                    return {
                        errors:
                            keys.reduce((prev, key) => {
                                const errors = this.form.get(key)?.errors
                                if (errors != null && arrayIsSet(Object.keys(errors))) {
                                    prev[key] = Object.keys(errors);
                                }
                                return prev;
                            }, {} as KeyStringValue<string[]>)
                    } as any
                } else {
                    return {};
                }
            })
        )
        this.formSingleStatement = this.fb.group({
            newStatements: undefined,
            newActuators: undefined
        })
        this.enablingStatements$ = (this.form as any).get('enablingStatements').valueChanges.pipe(
            startWith(undefined),
            map(() => this.enablingStatements)
        )
        this.statements$ = (this.form as any).get('statements').valueChanges.pipe(
            startWith(undefined),
            map(() => this.statements)
        )
        this.actuators$ = (this.form as any).get('actuators')?.valueChanges.pipe(
            startWith(undefined),
            map(() => this.actuators)
        )
        this.actuatorsDivided$ = this.actuators$.pipe(
            map((actuators) => {
                return this.dividedActuatorsFromType(actuators)
            })
        )
        this.else_actuators$ = (this.form as any).get('else_actuators')?.valueChanges.pipe(
            startWith(undefined),
            map(() => this.else_actuators)
        )
        this.elseActuatorsDivided$ = this.else_actuators$.pipe(
            map(actuators => {
                return this.dividedActuatorsFromType(actuators)
            })
        )
        if (this.data != null) {
            if (arrayIsSet(this.data.calendars)) {
                this.calendars = this.data.calendars;
            }
            if (arrayIsSet(this.data.sensorDataType)) {
                this.sensorDataType = this.data.sensorDataType;
            }
            if (arrayIsSet(this.data.elements)) {
                this.elements = this.data.elements;
            }
            if (arrayIsSet(this.data.elementsType)) {
                this.elementsType = this.data.elementsType;
            }
            if (this.data.elementsCategory != null) {
                this.lights = this.data.elementsCategory.lights;
                this.shutters = this.data.elementsCategory.shutters;
                this.switches = this.data.elementsCategory.switches;
                this.commands = this.data.elementsCategory.commands;
                this.linearActuators = this.data.elementsCategory.linearActuators;
                this.elements = this.data.elementsCategory.other;
            }
            if (this.data.sensorEvent != null) {
                if (stringIsSet(this.data.sensorEvent.name)) {
                    this.form.get('name')?.setValue(this.data.sensorEvent.name);
                    this.form.get('name')?.markAsPristine();
                }
                if (stringIsSet(this.data.sensorEvent.note)) {
                    this.form.get('note')?.setValue(this.data.sensorEvent.note);
                    this.form.get('note')?.markAsPristine();
                }
                if (this.data.sensorEvent.repeatEvent != null) {
                    this.form.get('repeatEvent')?.setValue(this.data.sensorEvent.repeatEvent);
                    this.form.get('repeatEvent')?.markAsPristine();
                }
                if (this.data.sensorEvent.notification != null) {
                    if (stringIsSet(this.data.sensorEvent.notification.subject)) {
                        this.form.get('notification')?.get('subject')?.setValue(this.data.sensorEvent.notification.subject);
                        this.form.get('notification')?.get('subject')?.markAsPristine();
                    }
                    if (stringIsSet(this.data.sensorEvent.notification.body)) {
                        this.form.get('notification')?.get('body')?.setValue(this.data.sensorEvent.notification.body);
                        this.form.get('notification')?.get('body')?.markAsPristine();
                    }
                }
                if (this.data.sensorEvent.type != null) {
                    this.form.get('type')?.setValue(this.data.sensorEvent.type);
                    this.form.get('type')?.markAsPristine();
                }
                if (arrayIsSet(this.data.sensorEvent.enablingStatements)) {
                    this.enablingStatements = this.cloneStatements(this.data.sensorEvent.enablingStatements)
                    this.form.get('enablingStatements')?.markAsPristine();

                }
                if (arrayIsSet(this.data.sensorEvent.statements)) {
                    this.statements = this.cloneStatements(this.data.sensorEvent.statements)
                    this.form.get('statements')?.markAsPristine();

                }
                if (arrayIsSet(this.data.sensorEvent.actuators)) {
                    this.actuators = [...this.data.sensorEvent.actuators];
                    this.form.get('actuators')?.markAsPristine();
                }
                if (arrayIsSet(this.data.sensorEvent.else_actuators)) {
                    this.else_actuators = [...this.data.sensorEvent.else_actuators];
                    this.form.get('actuators')?.markAsPristine();
                }
                if (this.data.sensorEvent.sendSms) {
                    this.form.get('sendSms')?.setValue(this.data.sensorEvent.sendSms);
                    this.form.get('sendSms')?.markAsPristine();
                }
                if (this.data.sensorEvent.notificationUsers) {
                    this.form.get('notificationUsers')?.setValue(this.data.sensorEvent.notificationUsers);
                    this.form.get('notificationUsers')?.markAsPristine();
                }
                if (this.data.sensorEvent.modalityNotification) {
                    this.form.get('modalityNotification')?.setValue(this.data.sensorEvent.modalityNotification);
                    this.form.get('modalityNotification')?.markAsPristine();
                } else {
                    this.form.get('modalityNotification')?.setValue(SensorEventModalityNotificationType.simplyReport);
                    this.form.get('modalityNotification')?.markAsPristine();
                }
            }
        }
    }


    get statements(): SensorEventStatement[][] | undefined {
        return this.form.get('statements')?.value
    }

    set statements(value) {
        if (arrayIsSet(value)) {
            this.form.get('statements')?.setValue(value)
            this.form.get('statements')?.markAsDirty();
        } else {
            this.form.get('statements')?.reset();
            this.form.get('statements')?.markAsDirty();
        }
    }
    get enablingStatements(): SensorEventStatement[][] | undefined {
        return this.form.get('enablingStatements')?.value
    }

    set enablingStatements(value) {
        if (arrayIsSet(value)) {
            this.form.get('enablingStatements')?.setValue(value)
            this.form.get('enablingStatements')?.markAsDirty();
        } else {
            this.form.get('enablingStatements')?.reset();
            this.form.get('enablingStatements')?.markAsDirty();
        }
    }

    set actuators(value) {
        if (arrayIsSet(value)) {
            this.form.get('actuators')?.setValue(value)
            this.form.get('actuators')?.markAsDirty();
        } else {
            this.form.get('actuators')?.reset();
            this.form.get('actuators')?.markAsDirty();
        }
    }

    get actuators(): SensorEventActuator[] | undefined {
        return this.form.get('actuators')?.value
    }

    set else_actuators(value) {
        if (arrayIsSet(value)) {
            this.form.get('else_actuators')?.setValue(value)
            this.form.get('else_actuators')?.markAsDirty();
        } else {
            this.form.get('else_actuators')?.reset();
            this.form.get('else_actuators')?.markAsDirty();
        }
    }

    get else_actuators(): SensorEventActuator[] | undefined {
        return this.form.get('else_actuators')?.value
    }

    dividedActuatorsFromType(actuators: SensorEventActuator[] | undefined) {

        if (arrayIsSet(actuators)) {
            let objDivided: KeyStringValue<{ actuator: SensorEventActuator, indexAll: number }[]> = {};
            actuators?.forEach((actuator, indexAll) => {
                this.keysActuatorsType.forEach(key => {
                    if (actuator.type === SensorEventActuatorType[key]) {
                        if (objDivided[key] == null) {
                            objDivided[key] = [];
                        }
                        objDivided[key].push({ actuator, indexAll });
                    }
                })

            })
            return Object.keys(objDivided).reduce((prev, key) => {
                const valueEnum = (SensorEventActuatorType as any)[key as any] as any;
                const title = 'SensorEvent.SensorEventActuatorType.' + (SensorEventActuatorType as any)[key as any];
                let icon;
                let svgIcon;
                if (valueEnum === SensorEventActuatorType.RELAY) {
                    icon = 'power_settings_new'
                } else if (valueEnum === SensorEventActuatorType.COMMAND) {
                    icon = 'settings'
                } else {
                    svgIcon = valueEnum.toString();
                }
                const actuators = objDivided[key];
                prev.divided.push({ title, icon, svgIcon, actuators })
                return prev;
            }, { all: actuators, divided: [] } as ActuatorsHtmlType)
        } else {
            return undefined;
        }
    }

    ngOnInit(): void {
    }

    clickSaveButton() {
        const updatedValues = Object.keys(this.form.value).reduce((updatedValues, key) => {
            if (this.form.get(key)?.dirty) {
                if (updatedValues == null) {
                    updatedValues = {};
                }
                updatedValues[key] = this.form.get(key)?.value;
            }
            return updatedValues;
        }, undefined as any)
        this.dialogRef.close(updatedValues);
    }

    closeWithoutSave() {
        if (this.form.dirty) {
            this.openDialogService.openDialogForDelete([this.ts.instant('alert.closeCancelOperation')]).pipe(
                filter(choose => choose == true)
            ).subscribe(() => {
                this.dialogRef.close(undefined)
            })
        } else {
            this.dialogRef.close(undefined)
        }
    }

    predicateGetAndChips(statement: SensorEventStatement, elements: ElementParse[] | undefined, calendars: CalendarEvent[]): ChipsHtmlType {
        let chipsValues: any = {
            sx: { infoNameTraduction: statement.infoName },
            operator: statement.op,
            dx: { value: undefined }
        };
        const configIsNumber = () => typeof statement.config == "number" || Number.isInteger(parseInt(statement.config as any))
        if (configIsNumber()) {
            chipsValues.dx = { value: { notTraduction: statement.config } };
        }
        // if (statement.type == SensorEventStatementType.FUNCTIONALITY_STATUS || statement.type == SensorEventStatementType.MODALITY) {
        if (statement.type == SensorEventStatementType.FUNCTIONALITY_STATUS) {

            const traductionSX = 'SensorEvent.SensorEventStatementType.' + statement.type
            // const traductionDX = (statement.type === SensorEventStatementType.MODALITY ? 'SensorEvent.SensorEventStatementModalityType.' : 'SensorEvent.SensorEventStatementFunctionality_StatusType.') + statement.config
            const traductionDX = 'SensorEvent.SensorEventStatementFunctionality_StatusType.' + statement.config
            chipsValues.sx = { value: { traduction: traductionSX } };
            chipsValues.dx = { value: { traduction: traductionDX } };

        } else if (arrayIsSet(elements)) {
            const device = elements.find(d => d.objectId === statement.sensorId)
            if (device != null) {
                chipsValues.sx['sensorName'] = device.name
            }
            if (statement.config != null && stringIsSet((statement.config as any).sensorId)) {
                chipsValues.dx = {};
                const sensorId = (statement.config as any).sensorId;
                const device = elements.find(d => d.objectId === sensorId)
                if (device != null) {
                    const infoName = (statement.config as any).infoName;
                    chipsValues.dx['sensorName'] = device.name;
                    const traduction = statement.type === SensorEventStatementType.SENSOR ? 'possibleValuesTypeElement.' + infoName : 'SensorEvent.SensorEventStatementType.' + SensorEventStatementType.SCENARIO
                    chipsValues.dx['infoNameTraduction'] = traduction;
                }
            }
        }
        if (statement.type == SensorEventStatementType.CALENDAR && statement && statement.config && 'calendarId' in (statement.config as any)) {
            const calendar = calendars?.find(c => c.objectId == (statement.config as any).calendarId)
            if (calendar) {
                chipsValues.sx['sensorName'] = calendar.name
            } else {
                chipsValues.sx['sensorName'] = (statement.config as any).calendarId
            }
        }
        if (
            statement.type === SensorEventStatementType.FUNCTIONALITY_STATUS ||
            statement.type === SensorEventStatementType.ACTIVITY_STATUS ||
            statement.type === SensorEventStatementType.PROGRAM ||
            statement.type === SensorEventStatementType.AGGREGATE
        ) {
            chipsValues.sx['type'] = `SensorEvent.SensorEventStatementType.${statement.type}`
        }

        if (
            statement.type === SensorEventStatementType.FUNCTIONALITY_STATUS ||
            statement.type === SensorEventStatementType.ACTIVITY_STATUS ||
            statement.type === SensorEventStatementType.PROGRAM
        ) {
            chipsValues.operator = undefined
        }

        if (statement.type === SensorEventStatementType.FUNCTIONALITY_STATUS) {
            chipsValues.dx.value = {
                traduction: `SensorEvent.SensorEventStatementFunctionality_StatusType.${statement.config}`,
                notTraduction: statement.config,
            }
        }


        if (statement.type === SensorEventStatementType.ACTIVITY_STATUS) {
            chipsValues.dx.value = {
                traduction: `SensorEvent.SensorEventStatementModalityType.${statement.config}`,
                notTraduction: statement.config,
            }
        }

        if (statement.type === SensorEventStatementType.AGGREGATE) {
            if (
                typeof statement.config === 'object' &&
                statement.config !== null &&
                'value' in statement.config &&
                'op' in statement.config
            ) {
                chipsValues.dx.value = {
                    notTraduction: (statement.config as { value: number }).value,
                }
                chipsValues.sx['op'] = `SensorEvent.StatementConfigOp.${statement.config.op}`
            }
        }

        return chipsValues
    }

    cloneStatements(statements: SensorEventStatement[][]): SensorEventStatement[][] {
        if (arrayIsSet(statements)) {
            return deepClone(statements)
        } else {
            return [];
        }

    }

    clickRemoveEnablingStatementOr(statements: SensorEventStatement[][], indexOr: number) {
        if (arrayIsSet(statements)) {
            const newStatements = this.cloneStatements(statements);
            newStatements.splice(indexOr, 1);
            this.enablingStatements = newStatements;
        }
    }

    clickAddEnablingStatementOr(statements: SensorEventStatement[][]) {
        const newStatements = this.cloneStatements(statements);
        newStatements.unshift([]);
        this.enablingStatements = deepClone(newStatements);
    }

    clickRemoveEnablingStatementAnd(statements: SensorEventStatement[][], indexOr: number, indexAnd: number,) {
        if (arrayIsSet(statements) && arrayIsSet(statements[indexOr])) {
            const newStatements = this.cloneStatements(statements);
            newStatements[indexOr].splice(indexAnd, 1);
            if (!arrayIsSet(newStatements[indexOr])) {
                newStatements.splice(indexOr, 1);
            }
            this.enablingStatements = newStatements;
        }
    }

    clickAddEnablingStatementAnd(statements: SensorEventStatement[][], indexOr: number) {
        const newStatements = this.cloneStatements(statements);
        if (!arrayIsSet(statements[indexOr])) {
            newStatements[indexOr] = []
        }
        if (this.ModifyEnablingStatementRef != null) {
            this.formSingleStatement.reset();
            this.modifyDialogRef = this.openDialogForm(this.ModifyEnablingStatementRef)
            this.modifyDialogRef.afterClosed().subscribe(a => {
                if (a != null && a.newStatements != null) {
                    newStatements[indexOr].unshift(a.newStatements)
                    this.enablingStatements = newStatements;
                }
            });
        }

    }
    clickEnablingStatementAnd(statements: SensorEventStatement[][], indexOr: number, indexAnd: number,) {
        this.modifyDialogRef?.close();
        const newStatements = this.cloneStatements(statements);
        if (!arrayIsSet(statements[indexOr])) {
            newStatements[indexOr] = []
        }
        if (arrayIsSet(statements) && arrayIsSet(statements[indexOr])) {
            const currentStatements = newStatements[indexOr][indexAnd] as any;
            if (currentStatements == undefined) {
                this.formSingleStatement.reset();
            } else {
                this.formSingleStatement.get('newStatements')?.setValue(currentStatements)
            }
        }

        if (this.ModifyEnablingStatementRef != null) {
            this.modifyDialogRef = this.openDialogForm(this.ModifyEnablingStatementRef)
            this.modifyDialogRef.afterClosed().subscribe(a => {
                if (a != null && a.newStatements != null) {
                    if (newStatements[indexOr][indexAnd] != null) {
                        newStatements[indexOr][indexAnd] = a.newStatements
                        this.enablingStatements = newStatements;
                    }
                }
            });
        }

        // this.statements = newStatements;
    }
    clickRemoveStatementOr(statements: SensorEventStatement[][], indexOr: number) {
        if (arrayIsSet(statements)) {
            const newStatements = this.cloneStatements(statements);
            newStatements.splice(indexOr, 1);
            this.statements = newStatements;
        }
    }

    clickAddStatementOr(statements: SensorEventStatement[][]) {
        const newStatements = this.cloneStatements(statements);
        newStatements.unshift([]);
        this.statements = newStatements;
    }

    clickRemoveStatementAnd(statements: SensorEventStatement[][], indexOr: number, indexAnd: number,) {
        if (arrayIsSet(statements) && arrayIsSet(statements[indexOr])) {
            const newStatements = this.cloneStatements(statements);
            newStatements[indexOr].splice(indexAnd, 1);
            if (!arrayIsSet(newStatements[indexOr])) {
                newStatements.splice(indexOr, 1);
            }
            this.statements = newStatements;
        }
    }

    clickAddStatementAnd(statements: SensorEventStatement[][], indexOr: number) {
        const newStatements = this.cloneStatements(statements);
        if (!arrayIsSet(statements[indexOr])) {
            newStatements[indexOr] = []
        }
        if (this.ModifyStatementRef != null) {
            this.formSingleStatement.reset();
            this.modifyDialogRef = this.openDialogForm(this.ModifyStatementRef)
            this.modifyDialogRef.afterClosed().subscribe(a => {
                if (a != null && a.newStatements != null) {
                    newStatements[indexOr].unshift(a.newStatements)
                    this.statements = newStatements;
                }
            });
        }

    }

    clickStatementAnd(statements: SensorEventStatement[][], indexOr: number, indexAnd: number,) {
        this.modifyDialogRef?.close();
        const newStatements = this.cloneStatements(statements);
        if (!arrayIsSet(statements[indexOr])) {
            newStatements[indexOr] = []
        }
        if (arrayIsSet(statements) && arrayIsSet(statements[indexOr])) {
            const currentStatements = newStatements[indexOr][indexAnd] as any;
            if (currentStatements == undefined) {
                this.formSingleStatement.reset();
            } else {
                this.formSingleStatement.get('newStatements')?.setValue(currentStatements)
            }
        }

        if (this.ModifyStatementRef != null) {
            this.modifyDialogRef = this.openDialogForm(this.ModifyStatementRef)
            this.modifyDialogRef.afterClosed().subscribe(a => {
                if (a != null && a.newStatements != null) {
                    if (newStatements[indexOr][indexAnd] != null) {
                        newStatements[indexOr][indexAnd] = a.newStatements
                        this.statements = newStatements;
                    }
                }
            });
        }

        // this.statements = newStatements;
    }

    openDialogForm(matDialogRef: TemplateRef<any>): MatDialogRef<any> {
        return this.dialog.open(matDialogRef, {
            width: '450px',
            maxWidth: '100%',
            autoFocus: false,
            exitAnimationDuration: 0.1
        })
    }

    predicateDisableAddOrButton(statements: SensorEventStatement[][]): boolean {
        return statements.some(stamentOr => {
            return !arrayIsSet(stamentOr)
        })
    }

    predicateDisableRemoveOrButton(statements: SensorEventStatement[][]): boolean {
        return !(arrayIsSet(statements) && statements.length > 1)
    }


    clickUpdate(key: string) {
        const values = this.formSingleStatement.get(key)?.value
        let updatedValue: any;
        if (this.formSingleStatement.get(key)?.dirty && values != null) {
            updatedValue = {};
            updatedValue[key] = { ...values };
        }
        this.modifyDialogRef?.close(updatedValue);
        this.formSingleStatement.reset();
    }

    closeDialog() {
        this.modifyDialogRef?.close(undefined);
        this.formSingleStatement.reset();
    }

    clickUpdateStatements() {
        this.clickUpdate('newStatements')
    }

    clickUpdateActuators() {
        this.clickUpdate('newActuators')
    }

    statusChange(event: FormControlStatus) {
        if (event == 'INVALID') {
            this.formSingleStatement.setErrors({ 'required': true })
        } else {
            this.formSingleStatement.setErrors(null);
        }
    }

    dialogActuators$() {
        if (this.ModifyActuatorsRef != null) {
            this.modifyDialogRef?.close();
            this.modifyDialogRef = this.openDialogForm(this.ModifyActuatorsRef);
            return this.modifyDialogRef.afterClosed().pipe(
                filter(a => a != null && a.newActuators != null)
            );
        } else {
            return EMPTY
        }
    }

    addActuators() {
        this.formSingleStatement.get('newActuators')?.reset();
        this.dialogActuators$().subscribe(a => {
            if (a != null && a.newActuators != null) {
                const newActuators = arrayIsSet(this.actuators) ? [...this.actuators] : [];
                newActuators.unshift(a.newActuators)
                this.actuators = newActuators
            }
        });
    }

    public addElseActuators() {
        // this.formSingleStatement.get('newActuators')?.reset();
        this.dialogActuators$().subscribe(a => {
            const newActuators = arrayIsSet(this.else_actuators) ? [...this.else_actuators] : [];
            newActuators.unshift(a.newActuators)
            this.else_actuators = newActuators
        })
    }

    clickBoxActuators(actuators: SensorEventActuator[], index: number) {
        this.formSingleStatement.get('newActuators')?.setValue(actuators[index]);
        this.dialogActuators$().subscribe(a => {
            if (a != null && a.newActuators != null) {
                const newActuators = arrayIsSet(this.actuators) ? [...this.actuators] : [];
                newActuators[index] = a.newActuators;
                this.actuators = newActuators
            }
        });

    }

    clickBoxElseActuators(actuators: SensorEventActuator[], index: number) {
        this.formSingleStatement.get('newActuators')?.setValue(actuators[index]);
        this.dialogActuators$().subscribe(a => {
            const newActuators = arrayIsSet(this.else_actuators) ? [...this.else_actuators] : [];
            newActuators[index] = a.newActuators;
            this.else_actuators = newActuators
        })
    }

    clickRemoveBoxActuators(actuators: SensorEventActuator[], index: number) {
        const newActuators = arrayIsSet(this.actuators) ? [...this.actuators] : [];
        newActuators.splice(index, 1);
        this.actuators = newActuators
    }

    clickRemoveBoxElseActuators(actuators: SensorEventActuator[], index: number) {
        const newActuators = arrayIsSet(this.else_actuators) ? [...this.else_actuators] : [];
        newActuators.splice(index, 1);
        this.else_actuators = newActuators
    }

    predicateSensoEventEnum(sensorEventType: SensorEventType) {
        let keys = Object.keys(sensorEventType).filter(x => !(parseInt(x) >= 0));
        return keys.map(key => {
            return {
                traduction: 'SensorEvent.SensorEventType.' + (sensorEventType as any)[key],
                valueForm: (sensorEventType as any)[key]
            }
        })
    }

    getIconTypePredicate(value: { valueForm: SensorEventType, traduction: string } | undefined): {
        icon?: string,
        svgIcon?: string,
        color: 'primary' | 'warn' | 'accent',
        tooltipText: string
    } | undefined {
        if (value == null || value.valueForm == null) {
            return undefined
        } else if (value.valueForm == SensorEventType.WINTER) {
            return { icon: 'ac_unit', color: "accent", tooltipText: 'sensorEventType.winter' }
        } else if (value.valueForm == SensorEventType.SUMMER) {
            return { icon: 'wb_sunny', color: 'accent', tooltipText: 'sensorEventType.summer' }
        } else if (value.valueForm == SensorEventType.ANY) {
            return { svgIcon: 'winterSummer', color: 'accent', tooltipText: 'sensorEventType.any' }
        } else {
            return { icon: 'alert', color: 'warn', tooltipText: 'sensorEventType.nonPrevisto' }
        }
    }

    openedExpansionPanel(key: 'actuators' | 'else_actuators', title: string) {
        setTimeout(() => {
            const obj: any = this.lastAccordionOpenedEmit.value != null ? { ...this.lastAccordionOpenedEmit.value } : {}
            obj[key] = title;
            this.lastAccordionOpenedEmit.next(obj);
        });
    }

    public getPossibleValuesUsersPredicate(values: { objectId: string, nome: string }[]) {
        if (arrayIsSet(values)) {
            return values.map(value => {
                const obj: PossibleValuesFormField = {
                    noTraduction: value.nome,
                    registerValue: { objectId: value.objectId },
                    objectId: value.objectId,
                }
                return obj
            })
        } else {
            return undefined
        }
    }


}
